<script setup>
import { computed, ref } from 'vue';
import { useIntervalFn } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useCurrentAgentPause } from 'dashboard/composables/useCurrentAgentPause';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const {
  blockingPause,
  activePause,
  hasBlockingPause,
  isPauseActionLoading,
  stopPause,
} = useCurrentAgentPause();

const now = ref(Date.now());

useIntervalFn(() => {
  now.value = Date.now();
}, 1000);

const pauseEndsAt = computed(() => {
  return blockingPause.value
    ? new Date(
        blockingPause.value.ends_at || blockingPause.value.end_at
      ).getTime()
    : null;
});

const pauseStartsAt = computed(() => {
  const startAt =
    activePause.value?.started_at || blockingPause.value?.start_at || null;

  return startAt ? new Date(startAt).getTime() : null;
});

const remainingMs = computed(() => {
  if (!pauseEndsAt.value) return 0;

  return pauseEndsAt.value - now.value;
});

const totalDurationMs = computed(() => {
  if (!pauseStartsAt.value || !pauseEndsAt.value) return 0;

  return Math.max(pauseEndsAt.value - pauseStartsAt.value, 0);
});

const warningThresholdMs = computed(() => {
  if (!totalDurationMs.value) return 60 * 1000;

  const twentyPercent = Math.floor(totalDurationMs.value * 0.2);
  return Math.min(5 * 60 * 1000, Math.max(60 * 1000, twentyPercent));
});

const pauseSeverity = computed(() => {
  if (remainingMs.value < 0) return 'overdue';
  if (remainingMs.value <= warningThresholdMs.value) return 'warning';

  return 'normal';
});

const severityClasses = computed(() => {
  if (pauseSeverity.value === 'overdue') {
    return {
      ring: 'border-n-ruby-8 shadow-[0_0_0_12px_rgba(226,70,102,0.08)]',
      halo: 'bg-n-ruby-5/70',
      badge: 'bg-n-ruby-5 text-n-ruby-11 outline outline-1 outline-n-ruby-7/60',
      accent: 'text-n-ruby-11',
      panel:
        'bg-gradient-to-br from-n-ruby-5/50 via-n-surface-2 to-n-surface-2',
      pulse: 'animate-pulse',
    };
  }

  if (pauseSeverity.value === 'warning') {
    return {
      ring: 'border-n-amber-8 shadow-[0_0_0_12px_rgba(255,194,77,0.08)]',
      halo: 'bg-n-amber-5/70',
      badge:
        'bg-n-amber-5 text-n-amber-11 outline outline-1 outline-n-amber-8/60',
      accent: 'text-n-amber-11',
      panel:
        'bg-gradient-to-br from-n-amber-5/40 via-n-surface-2 to-n-surface-2',
      pulse: 'animate-pulse',
    };
  }

  return {
    ring: 'border-n-teal-8 shadow-[0_0_0_12px_rgba(27,164,125,0.08)]',
    halo: 'bg-n-teal-5/60',
    badge: 'bg-n-teal-5 text-n-teal-11 outline outline-1 outline-n-teal-8/60',
    accent: 'text-n-teal-11',
    panel: 'bg-gradient-to-br from-n-teal-5/30 via-n-surface-2 to-n-surface-2',
    pulse: '',
  };
});

const formattedCountdown = computed(() => {
  const value = Math.abs(remainingMs.value);
  const totalSeconds = Math.floor(value / 1000);
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
    2,
    '0'
  );
  const seconds = String(totalSeconds % 60).padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
});

const formattedElapsed = computed(() => {
  if (!pauseStartsAt.value) return '00:00:00';

  const elapsedMs = Math.max(now.value - pauseStartsAt.value, 0);
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
    2,
    '0'
  );
  const seconds = String(totalSeconds % 60).padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
});

const overlayTitle = computed(() => {
  return activePause.value
    ? t('AGENT_MGMT.PAUSE_OVERLAY.TITLE')
    : t('AGENT_MGMT.PAUSE_OVERLAY.SCHEDULED_TITLE');
});

const overlayDescription = computed(() => {
  return activePause.value
    ? t('AGENT_MGMT.PAUSE_OVERLAY.DESCRIPTION')
    : t('AGENT_MGMT.PAUSE_OVERLAY.SCHEDULED_DESCRIPTION');
});

const overlayStatus = computed(() => {
  if (pauseSeverity.value === 'overdue') {
    return t('AGENT_MGMT.PAUSE_OVERLAY.STATUS.OVERDUE');
  }

  if (pauseSeverity.value === 'warning') {
    return t('AGENT_MGMT.PAUSE_OVERLAY.STATUS.WARNING');
  }

  return t('AGENT_MGMT.PAUSE_OVERLAY.STATUS.NORMAL');
});

const countdownLabel = computed(() => {
  return remainingMs.value < 0
    ? t('AGENT_MGMT.PAUSE_OVERLAY.OVERDUE_BY')
    : t('AGENT_MGMT.PAUSE_OVERLAY.ENDS_IN');
});

const formatDateTime = value => {
  if (!value) return '';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 scale-[0.985]"
    enter-to-class="opacity-100 scale-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100 scale-100"
    leave-to-class="opacity-0 scale-[0.985]"
  >
    <div
      v-if="hasBlockingPause"
      class="fixed inset-0 z-[10000] bg-n-alpha-black3 backdrop-blur-sm"
    >
      <div
        class="relative flex h-full w-full items-center justify-center overflow-auto px-4 py-8"
      >
        <section
          class="w-full max-w-4xl overflow-hidden rounded-[32px] bg-n-surface-2/95 shadow-2xl outline outline-1 outline-n-weak"
          :class="severityClasses.panel"
        >
          <div
            class="grid gap-10 px-6 py-8 lg:grid-cols-[1.1fr_0.9fr] lg:px-10"
          >
            <div class="flex flex-col justify-between gap-8">
              <div class="space-y-4">
                <div
                  class="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em]"
                  :class="[severityClasses.badge, severityClasses.pulse]"
                >
                  <span class="size-2 rounded-full bg-current" />
                  {{ overlayStatus }}
                </div>

                <div class="space-y-3">
                  <h2
                    class="text-3xl font-semibold tracking-tight text-n-slate-12"
                  >
                    {{ overlayTitle }}
                  </h2>
                  <p class="max-w-2xl text-base leading-7 text-n-slate-11">
                    {{ overlayDescription }}
                  </p>
                </div>

                <div
                  class="rounded-2xl bg-n-surface-1/80 p-4 outline outline-1 outline-n-weak"
                >
                  <div class="text-lg font-medium text-n-slate-12">
                    {{ blockingPause.label }}
                  </div>
                  <div
                    class="mt-3 grid gap-3 text-sm text-n-slate-11 md:grid-cols-3"
                  >
                    <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                      <div
                        class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
                      >
                        {{ t('AGENT_MGMT.PAUSE_OVERLAY.STARTED_AT') }}
                      </div>
                      <div class="mt-1 text-sm text-n-slate-12">
                        {{
                          formatDateTime(
                            activePause?.started_at || blockingPause.start_at
                          )
                        }}
                      </div>
                    </div>
                    <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                      <div
                        class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
                      >
                        {{ t('AGENT_MGMT.PAUSE_OVERLAY.PLANNED_END') }}
                      </div>
                      <div class="mt-1 text-sm text-n-slate-12">
                        {{
                          formatDateTime(
                            blockingPause.ends_at || blockingPause.end_at
                          )
                        }}
                      </div>
                    </div>
                    <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                      <div
                        class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
                      >
                        {{ t('AGENT_MGMT.PAUSE_OVERLAY.ELAPSED') }}
                      </div>
                      <div class="mt-1 font-medium text-n-slate-12">
                        {{ formattedElapsed }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex flex-wrap items-center gap-3">
                <Button
                  v-if="activePause"
                  size="lg"
                  :color="pauseSeverity === 'overdue' ? 'ruby' : 'blue'"
                  :label="t('AGENT_MGMT.PAUSE_OVERLAY.END')"
                  icon="i-lucide-circle-stop"
                  :is-loading="isPauseActionLoading"
                  @click="stopPause"
                />
                <div
                  v-else
                  class="inline-flex items-center gap-2 rounded-2xl bg-n-alpha-2 px-4 py-3 text-sm text-n-slate-11 outline outline-1 outline-n-weak"
                >
                  <Icon icon="i-lucide-calendar-clock" class="size-4" />
                  {{ t('AGENT_MGMT.PAUSE_OVERLAY.SCHEDULED_HINT') }}
                </div>
              </div>
            </div>

            <div class="flex items-center justify-center">
              <div
                class="relative flex size-[18rem] items-center justify-center rounded-full border-[10px] bg-n-surface-1/80"
                :class="[severityClasses.ring, severityClasses.pulse]"
              >
                <div
                  class="absolute inset-5 rounded-full blur-2xl"
                  :class="[severityClasses.halo, severityClasses.pulse]"
                />
                <div
                  class="absolute inset-4 rounded-full bg-n-surface-2/90 outline outline-1 outline-n-weak"
                />
                <div
                  class="relative z-10 flex flex-col items-center text-center"
                >
                  <div
                    class="text-xs font-medium uppercase tracking-[0.22em] text-n-slate-10"
                  >
                    {{ countdownLabel }}
                  </div>
                  <div
                    class="mt-4 text-5xl font-semibold tracking-tight text-n-slate-12 tabular-nums"
                    :class="severityClasses.accent"
                  >
                    {{ formattedCountdown }}
                  </div>
                  <div class="mt-5 text-sm text-n-slate-11">
                    {{ t('AGENT_MGMT.PAUSE_OVERLAY.RETURN_HINT') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </Transition>
</template>
